/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getDoctors } from 'app/entities/doctor/doctor.reducer';
import { getEntities as getDiagLabs } from 'app/entities/diag-lab/diag-lab.reducer';
import { getEntities as getImageTests } from 'app/entities/image-tests/image-tests.reducer';
import { getEntities as getDiagLabPanels } from 'app/entities/diag-lab-panel/diag-lab-panel.reducer';
import { getEntities as getLabTestItems } from 'app/entities/lab-test-item/lab-test-item.reducer';
import { AssociationType } from 'app/shared/model/enumerations/association-type.model';
import { createEntity, getEntity, reset, updateEntity } from './radiology-report-template.reducer';

export const RadiologyReportTemplateUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const doctors = useAppSelector(state => state.doctor.entities);
  const diagLabs = useAppSelector(state => state.diagLab.entities);
  const imageTests = useAppSelector(state => state.imageTests.entities);
  const diagLabPanels = useAppSelector(state => state.diagLabPanel.entities);
  const labTestItems = useAppSelector(state => state.labTestItem.entities);
  const radiologyReportTemplateEntity = useAppSelector(state => state.radiologyReportTemplate.entity);
  const loading = useAppSelector(state => state.radiologyReportTemplate.loading);
  const updating = useAppSelector(state => state.radiologyReportTemplate.updating);
  const updateSuccess = useAppSelector(state => state.radiologyReportTemplate.updateSuccess);
  const associationTypeValues = Object.keys(AssociationType);

  const handleClose = () => {
    navigate(`/radiology-report-template${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getDoctors({}));
    dispatch(getDiagLabs({}));
    dispatch(getImageTests({}));
    dispatch(getDiagLabPanels({}));
    dispatch(getLabTestItems({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.dateVoided !== undefined && typeof values.dateVoided !== 'number') {
      values.dateVoided = Number(values.dateVoided);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }

    const entity = {
      ...radiologyReportTemplateEntity,
      ...values,
      doctor: doctors.find(it => it.id.toString() === values.doctor?.toString()),
      lab: diagLabs.find(it => it.id.toString() === values.lab?.toString()),
      test: imageTests.find(it => it.id.toString() === values.test?.toString()),
      panel: diagLabPanels.find(it => it.id.toString() === values.panel?.toString()),
      testItem: labTestItems.find(it => it.id.toString() === values.testItem?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          associationType: 'PATHOLOGY',
          ...radiologyReportTemplateEntity,
          doctor: radiologyReportTemplateEntity?.doctor?.id,
          lab: radiologyReportTemplateEntity?.lab?.id,
          test: radiologyReportTemplateEntity?.test?.id,
          panel: radiologyReportTemplateEntity?.panel?.id,
          testItem: radiologyReportTemplateEntity?.testItem?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.radiologyReportTemplate.home.createOrEditLabel" data-cy="RadiologyReportTemplateCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.radiologyReportTemplate.home.createOrEditLabel">
              Create or edit a RadiologyReportTemplate
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="radiology-report-template-id"
                  label={translate('tinyMedicRestApp.radiologyReportTemplate.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.charset')}
                id="radiology-report-template-charset"
                name="charset"
                data-cy="charset"
                type="text"
                validate={{
                  maxLength: { value: 12, message: translate('entity.validation.maxlength', { max: 12 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.path')}
                id="radiology-report-template-path"
                name="path"
                data-cy="path"
                type="text"
                validate={{
                  maxLength: { value: 500, message: translate('entity.validation.maxlength', { max: 500 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.reportBody')}
                id="radiology-report-template-reportBody"
                name="reportBody"
                data-cy="reportBody"
                type="textarea"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsTitle')}
                id="radiology-report-template-dcTermsTitle"
                name="dcTermsTitle"
                data-cy="dcTermsTitle"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsDescription')}
                id="radiology-report-template-dcTermsDescription"
                name="dcTermsDescription"
                data-cy="dcTermsDescription"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsIdentifier')}
                id="radiology-report-template-dcTermsIdentifier"
                name="dcTermsIdentifier"
                data-cy="dcTermsIdentifier"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsType')}
                id="radiology-report-template-dcTermsType"
                name="dcTermsType"
                data-cy="dcTermsType"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsLanguage')}
                id="radiology-report-template-dcTermsLanguage"
                name="dcTermsLanguage"
                data-cy="dcTermsLanguage"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsPublisher')}
                id="radiology-report-template-dcTermsPublisher"
                name="dcTermsPublisher"
                data-cy="dcTermsPublisher"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsRights')}
                id="radiology-report-template-dcTermsRights"
                name="dcTermsRights"
                data-cy="dcTermsRights"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsLicense')}
                id="radiology-report-template-dcTermsLicense"
                name="dcTermsLicense"
                data-cy="dcTermsLicense"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsDate')}
                id="radiology-report-template-dcTermsDate"
                name="dcTermsDate"
                data-cy="dcTermsDate"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dcTermsCreator')}
                id="radiology-report-template-dcTermsCreator"
                name="dcTermsCreator"
                data-cy="dcTermsCreator"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.voided')}
                id="radiology-report-template-voided"
                name="voided"
                data-cy="voided"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.dateVoided')}
                id="radiology-report-template-dateVoided"
                name="dateVoided"
                data-cy="dateVoided"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.microSec')}
                id="radiology-report-template-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.timestampEntry')}
                id="radiology-report-template-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.lastUpdated')}
                id="radiology-report-template-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.cursor')}
                id="radiology-report-template-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.active')}
                id="radiology-report-template-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiologyReportTemplate.associationType')}
                id="radiology-report-template-associationType"
                name="associationType"
                data-cy="associationType"
                type="select"
              >
                {associationTypeValues.map(associationType => (
                  <option value={associationType} key={associationType}>
                    {translate(`tinyMedicRestApp.AssociationType.${associationType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                id="radiology-report-template-doctor"
                name="doctor"
                data-cy="doctor"
                label={translate('tinyMedicRestApp.radiologyReportTemplate.doctor')}
                type="select"
              >
                <option value="" key="0" />
                {doctors
                  ? doctors.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiology-report-template-lab"
                name="lab"
                data-cy="lab"
                label={translate('tinyMedicRestApp.radiologyReportTemplate.lab')}
                type="select"
              >
                <option value="" key="0" />
                {diagLabs
                  ? diagLabs.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiology-report-template-test"
                name="test"
                data-cy="test"
                label={translate('tinyMedicRestApp.radiologyReportTemplate.test')}
                type="select"
              >
                <option value="" key="0" />
                {imageTests
                  ? imageTests.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiology-report-template-panel"
                name="panel"
                data-cy="panel"
                label={translate('tinyMedicRestApp.radiologyReportTemplate.panel')}
                type="select"
              >
                <option value="" key="0" />
                {diagLabPanels
                  ? diagLabPanels.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiology-report-template-testItem"
                name="testItem"
                data-cy="testItem"
                label={translate('tinyMedicRestApp.radiologyReportTemplate.testItem')}
                type="select"
              >
                <option value="" key="0" />
                {labTestItems
                  ? labTestItems.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/radiology-report-template" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default RadiologyReportTemplateUpdate;
